import { Link } from 'react-router-dom';
import { Linkedin, Github } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FooterProps {
  /** Additional CSS classes */
  className?: string;
}

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
  /** Show "coming soon" styling */
  comingSoon?: boolean;
}

const projectLinks: FooterLink[] = [
  { label: 'Cosmos', href: '/cosmos' },
  {
    label: 'LogicLoom',
    href: 'https://github.com/kelleysd-apps/LogicLoom',
    external: true,
  },
  {
    label: 'LeadBench',
    href: 'https://github.com/kelleysd-apps/leadbench',
    external: true,
  },
];

const legalLinks: FooterLink[] = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Contact', href: '/contact' },
];

const socialLinks = [
  {
    platform: 'linkedin' as const,
    href: 'https://www.linkedin.com/in/brian-kelley619/',
    label: 'LinkedIn',
    icon: Linkedin,
  },
  {
    platform: 'github' as const,
    href: 'https://github.com/kelleysd-apps',
    label: 'GitHub',
    icon: Github,
  },
];

/**
 * Footer Component (Dark Theme - Kelley SD Pivot)
 *
 * Three-column layout on desktop:
 *  - Left: Logo + company name + tagline + social icons
 *  - Center: Project links (Cosmos, LogicLoom, LeadBench)
 *  - Right: Legal links (Privacy, Terms, Contact)
 *
 * No newsletter signup. Dark bg-surface with border-subtle top border.
 *
 * @example
 * ```tsx
 * <Footer />
 * ```
 */
export function Footer({ className }: FooterProps) {
  const renderLink = (link: FooterLink) => {
    const baseClasses = cn(
      'text-sm transition-colors duration-200',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface rounded-sm'
    );

    if (link.comingSoon) {
      return (
        <span
          className={cn(baseClasses, 'text-text-muted cursor-default')}
          title="Coming soon"
        >
          {link.label}
          <span className="ml-1.5 text-xs text-text-muted">(soon)</span>
        </span>
      );
    }

    if (link.external) {
      return (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(baseClasses, 'text-text-secondary hover:text-text-primary')}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        to={link.href}
        className={cn(baseClasses, 'text-text-secondary hover:text-text-primary')}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <footer
      className={cn(
        'bg-bg-surface border-t border-border-subtle',
        className
      )}
      role="contentinfo"
    >
      <div className="mx-auto max-w-container-xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {/* Left: Brand */}
          <div>
            <Link
              to="/"
              className="inline-block mb-3 hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber rounded-md"
            >
              <span className="font-display text-lg font-semibold tracking-[0.15em] uppercase text-text-primary">
                KELLEY SD
              </span>
            </Link>
            <p className="text-sm text-text-secondary mb-1">
              Kelley Solutions Development
            </p>
            <p className="text-sm text-text-muted italic mb-6">
              Code. Intelligence. Craft.
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.platform}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'p-2 rounded-lg',
                      'text-text-muted hover:text-text-primary hover:bg-bg-elevated',
                      'transition-all duration-200',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber'
                    )}
                    aria-label={social.label}
                  >
                    <Icon size={18} aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Center: Projects */}
          <div>
            <h3 className="text-sm font-display font-semibold text-text-primary mb-4 tracking-wide uppercase">
              Projects
            </h3>
            <ul className="space-y-3">
              {projectLinks.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Right: Legal */}
          <div>
            <h3 className="text-sm font-display font-semibold text-text-primary mb-4 tracking-wide uppercase">
              Legal
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-border-subtle">
          <p className="text-sm text-text-muted text-center">
            &copy; 2026 Kelley SD. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
